import React from 'react';
import { Gift, Sparkles } from 'lucide-react';
import { GiftCardSettings, defaultGiftCardSettings } from '../services/giftCardSettingsService';

// Fixed base size for consistent rendering across all views
const GIFT_CARD_BASE_WIDTH = 600;
const GIFT_CARD_BASE_HEIGHT = 378; // 600 / 1.586 ≈ 378

interface GiftCardPreviewProps {
  settings?: Partial<GiftCardSettings> | null;
  businessLogoUrl?: string | null;
  code?: string;
  recipientName?: string;
  showSampleData?: boolean;
  baseWidth?: number; // Optional override for base width (defaults to 600)
}

export default function GiftCardPreview({
  settings,
  businessLogoUrl,
  code,
  recipientName,
  showSampleData = false,
  baseWidth = GIFT_CARD_BASE_WIDTH
}: GiftCardPreviewProps) {
  // Merge with defaults
  const cardSettings = {
    ...defaultGiftCardSettings,
    ...settings
  } as GiftCardSettings;

  // Calculate scale factor based on base width
  const scale = baseWidth / GIFT_CARD_BASE_WIDTH;
  
  // Fixed pixel values that scale proportionally
  const getScaledSize = (baseSize: number) => Math.round(baseSize * scale);
  
  // Fixed sizes (at 600px base width)
  const SIZES = {
    // Logo sizes
    LOGO_SMALL: 32,
    LOGO_MEDIUM: 48,
    LOGO_MAX_HEIGHT: 48,
    
    // Typography
    TITLE_FONT_SIZE: 32,
    MESSAGE_FONT_SIZE: 14,
    CODE_FONT_SIZE: 14,
    
    // Spacing
    PADDING: 32,
    TITLE_MARGIN_BOTTOM: 8,
    MESSAGE_MARGIN_BOTTOM: 24,
    CODE_PADDING_BOTTOM: 16,
    
    // Decorative elements
    DECORATIVE_CIRCLE_LARGE: 256,
    DECORATIVE_CIRCLE_SMALL: 192,
    ACCENT_BAR_HEIGHT: 8,
    
    // Icon sizes
    GIFT_ICON_SIZE: 32,
    GIFT_ICON_PADDING: 12,
    SPARKLES_LARGE: 32,
    SPARKLES_SMALL: 24,
  };

  // Sample data for preview
  const displayCode = code || (showSampleData ? 'GIFT-XXXX-XXXX-XXXX' : '');
  const displayRecipient = recipientName || (showSampleData ? 'Sample Recipient' : '');
  
  // Use actual logo or sample logo for preview
  const displayLogoUrl = businessLogoUrl || (showSampleData && cardSettings.include_logo ? 'https://via.placeholder.com/200x60/3B82F6/FFFFFF?text=Your+Logo' : null);

  // Get border radius value in pixels
  const getBorderRadius = () => {
    switch (cardSettings.border_radius) {
      case 'sharp': return '0px';
      case 'rounded': return getScaledSize(16) + 'px';
      case 'pill': return getScaledSize(48) + 'px';
      default: return getScaledSize(16) + 'px';
    }
  };

  // Get font family style
  const getFontFamily = () => {
    switch (cardSettings.font_family) {
      case 'inter': return 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      case 'roboto': return 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      case 'playfair': return '"Playfair Display", Georgia, serif';
      case 'poppins': return 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      case 'montserrat': return 'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      default: return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    }
  };

  // Get logo size in pixels
  const getLogoSize = () => {
    const baseSize = cardSettings.logo_size === 'small' ? SIZES.LOGO_SMALL : SIZES.LOGO_MEDIUM;
    return getScaledSize(baseSize);
  };

  // Get logo position styles
  const getLogoPosition = () => {
    const position = cardSettings.logo_position;
    const padding = getScaledSize(SIZES.PADDING);
    
    let top = 'auto';
    let bottom = 'auto';
    let left = 'auto';
    let right = 'auto';
    let transform = 'none';

    if (position.startsWith('top-')) {
      top = padding + 'px';
    } else if (position.startsWith('center-')) {
      top = '50%';
      transform = 'translateY(-50%)';
    } else if (position.startsWith('bottom-')) {
      bottom = padding + 'px';
    }

    if (position.endsWith('-left')) {
      left = padding + 'px';
      right = 'auto';
    } else if (position.endsWith('-center') || position === 'center') {
      left = '50%';
      right = 'auto';
      transform = transform === 'translateY(-50%)' ? 'translate(-50%, -50%)' : 'translateX(-50%)';
    } else if (position.endsWith('-right')) {
      right = padding + 'px';
      left = 'auto';
    }

    return { top, bottom, left, right, transform };
  };

  // Determine if we should use light or dark text based on background
  const isLightBackground = (color: string) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const textColor = isLightBackground(cardSettings.background_color) 
    ? cardSettings.text_color 
    : '#FFFFFF';

  const logoPosition = getLogoPosition();
  const logoSize = getLogoSize();
  const logoMaxHeight = getScaledSize(cardSettings.logo_max_height || SIZES.LOGO_MAX_HEIGHT);
  const padding = getScaledSize(SIZES.PADDING);
  const titleFontSize = getScaledSize(SIZES.TITLE_FONT_SIZE);
  const messageFontSize = getScaledSize(SIZES.MESSAGE_FONT_SIZE);
  const codeFontSize = getScaledSize(SIZES.CODE_FONT_SIZE);
  const titleMarginBottom = getScaledSize(SIZES.TITLE_MARGIN_BOTTOM);
  const messageMarginBottom = getScaledSize(SIZES.MESSAGE_MARGIN_BOTTOM);
  const codePaddingBottom = getScaledSize(SIZES.CODE_PADDING_BOTTOM);
  const giftIconSize = getScaledSize(SIZES.GIFT_ICON_SIZE);
  const giftIconPadding = getScaledSize(SIZES.GIFT_ICON_PADDING);

  return (
    <div 
      className="relative overflow-hidden"
      style={{ 
        width: baseWidth + 'px',
        height: Math.round(baseWidth / 1.586) + 'px',
        backgroundColor: cardSettings.background_color,
        borderRadius: getBorderRadius(),
        fontFamily: getFontFamily(),
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}
    >
      {/* Background gradient overlay for modern style */}
      {cardSettings.card_style === 'modern' && (
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${cardSettings.primary_color} 0%, ${cardSettings.secondary_color} 100%)`,
            opacity: 0.1,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Decorative elements based on style */}
      {cardSettings.show_decorative_elements && (
        <>
          {cardSettings.card_style === 'modern' && (
            <>
              <div 
                className="absolute rounded-full"
                style={{ 
                  top: 0,
                  right: 0,
                  width: getScaledSize(SIZES.DECORATIVE_CIRCLE_LARGE) + 'px',
                  height: getScaledSize(SIZES.DECORATIVE_CIRCLE_LARGE) + 'px',
                  backgroundColor: cardSettings.primary_color,
                  opacity: 0.2,
                  filter: 'blur(60px)',
                  transform: 'translate(50%, -50%)'
                }}
              />
              <div 
                className="absolute rounded-full"
                style={{ 
                  bottom: 0,
                  left: 0,
                  width: getScaledSize(SIZES.DECORATIVE_CIRCLE_SMALL) + 'px',
                  height: getScaledSize(SIZES.DECORATIVE_CIRCLE_SMALL) + 'px',
                  backgroundColor: cardSettings.accent_color,
                  opacity: 0.2,
                  filter: 'blur(60px)',
                  transform: 'translate(-50%, 50%)'
                }}
              />
            </>
          )}
          {cardSettings.card_style === 'elegant' && (
            <>
              <div className="absolute inset-0" style={{ opacity: 0.05 }}>
                <div style={{ position: 'absolute', top: getScaledSize(16) + 'px', right: getScaledSize(16) + 'px' }}>
                  <Sparkles 
                    style={{ 
                      color: cardSettings.accent_color,
                      width: getScaledSize(SIZES.SPARKLES_LARGE) + 'px',
                      height: getScaledSize(SIZES.SPARKLES_LARGE) + 'px'
                    }} 
                  />
                </div>
                <div style={{ position: 'absolute', bottom: getScaledSize(16) + 'px', left: getScaledSize(16) + 'px' }}>
                  <Sparkles 
                    style={{ 
                      color: cardSettings.accent_color,
                      width: getScaledSize(SIZES.SPARKLES_SMALL) + 'px',
                      height: getScaledSize(SIZES.SPARKLES_SMALL) + 'px'
                    }} 
                  />
                </div>
              </div>
            </>
          )}
          {cardSettings.card_style === 'playful' && (
            <div className="absolute inset-0" style={{ opacity: 0.1 }}>
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    backgroundColor: i % 2 === 0 ? cardSettings.primary_color : cardSettings.accent_color,
                    width: getScaledSize(Math.random() * 40 + 20) + 'px',
                    height: getScaledSize(Math.random() * 40 + 20) + 'px',
                    top: Math.random() * 100 + '%',
                    left: Math.random() * 100 + '%',
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Logo - Absolute positioned for full control */}
      {cardSettings.include_logo && displayLogoUrl && (
        <div 
          className="absolute flex pointer-events-none"
          style={{
            top: logoPosition.top,
            bottom: logoPosition.bottom,
            left: logoPosition.left,
            right: logoPosition.right,
            transform: logoPosition.transform,
            zIndex: 1
          }}
        >
          <img 
            src={displayLogoUrl} 
            alt="Business Logo" 
            style={{ 
              height: logoSize + 'px',
              maxHeight: logoMaxHeight + 'px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Content Container */}
      <div 
        className="relative flex flex-col"
        style={{ 
          height: '100%',
          padding: padding + 'px',
          zIndex: 2
        }}
      >
        {/* Gift Icon if no logo */}
        {(!cardSettings.include_logo || !displayLogoUrl) && (
          <div className="flex justify-center" style={{ marginBottom: getScaledSize(16) + 'px' }}>
            <div 
              className="rounded-full"
              style={{ 
                padding: giftIconPadding + 'px',
                backgroundColor: cardSettings.primary_color
              }}
            >
              <Gift 
                style={{ 
                  width: giftIconSize + 'px',
                  height: giftIconSize + 'px',
                  color: '#FFFFFF'
                }} 
              />
            </div>
          </div>
        )}

        {/* Card Title */}
        <div 
          style={{
            marginBottom: titleMarginBottom + 'px',
            textAlign: cardSettings.card_title_position === 'left' ? 'left' :
                      cardSettings.card_title_position === 'right' ? 'right' : 'center'
          }}
        >
          <h2 
            style={{ 
              fontSize: titleFontSize + 'px',
              fontWeight: 'bold',
              color: textColor,
              margin: 0,
              lineHeight: 1.2
            }}
          >
            {cardSettings.card_title}
          </h2>
        </div>

        {/* Greeting Message */}
        <div 
          style={{
            marginBottom: messageMarginBottom + 'px',
            textAlign: cardSettings.greeting_message_position === 'left' ? 'left' :
                      cardSettings.greeting_message_position === 'right' ? 'right' : 'center'
          }}
        >
          <p 
            style={{ 
              fontSize: messageFontSize + 'px',
              color: textColor,
              opacity: 0.9,
              margin: 0,
              lineHeight: 1.5
            }}
          >
            {cardSettings.greeting_message}
          </p>
        </div>

        {/* Spacer to push card number to bottom */}
        <div style={{ flex: 1 }}></div>

        {/* Card Number - Fixed at bottom center */}
        {displayCode && (
          <div 
            className="text-center"
            style={{ 
              marginTop: 'auto',
              paddingBottom: codePaddingBottom + 'px'
            }}
          >
            <p 
              style={{ 
                fontFamily: '"Courier New", Courier, monospace',
                fontSize: codeFontSize + 'px',
                fontWeight: 600,
                color: textColor,
                margin: 0,
                letterSpacing: '2px',
                lineHeight: 1.2
              }}
            >
              {displayCode}
            </p>
          </div>
        )}
      </div>

      {/* Accent bar based on style */}
      {cardSettings.card_style === 'classic' && (
        <div 
          className="absolute bottom-0 left-0 right-0"
          style={{ 
            height: getScaledSize(SIZES.ACCENT_BAR_HEIGHT) + 'px',
            backgroundColor: cardSettings.accent_color
          }}
        />
      )}
    </div>
  );
}
